/* eslint-disable camelcase */
import { PleasedArticleAction } from '../bot/flow-frameworks/ChatMessageActions.static';
import { ApiOpenHelpEntry, OpenHelpEntry } from '../entryLog';
import { ReportedEvent } from '../reporting';
import { PleasedUserInfo } from '../types';
import { TransStatement } from './model';

export interface PredictiveSuggestionConfig {
  id: number;
  articleId: number | null;
  brandId: number;
  connectToAgent: boolean;
  language: string;
  message: string;
  predictiveSuggestion: string;
  showLiveChat: boolean;
  showOpenRequest: boolean;
  showVoiceChat: boolean;
  isActive: boolean;
}

export interface BrandConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  brandId: number;
  showWelcomeMessage: boolean;
  welcomeMessage: string;
  widgetLogoUri: string;
  widgetTitle: string;
  aiEnabled?: boolean | null;
  abTestPercentage?: number | null;
  aiAbTestingEnabled?: boolean | null;
  openAiDown?: boolean | null;
  aiEndUserComeBackTime?: number | null;
  aiWelcomeMessage?: string | null;
  aiTransactionEnabled?: boolean | null;
  aiTransactionAbRatio?: number | null;
  aiBetEnabled?: boolean | null;
  aiBetAbRatio: number | null;

  timedAiFeedbackConfig: {
    enableTimedAiFeedback?: boolean;
    timedAiFeedbackNoMessagesTimeInSeconds?: number;
    timedAiFeedbackResumeMessageText?: string;
    timedAiFeedbackConfirmationMessageText?: string;
  };

  aiGuestUsersAbRatio?: number | null;
  aiGuestUsersEnabled?: boolean | null;
  aiGuestUsersMessage?: string | null;
  aiGuestUsersResponseText?: string | null;

  new: boolean;
  createdBy: number;
}

export interface BrandCategory {
  id: number;
  title: string;
  iconUri: string;
  showToEndUser: boolean;
}

export interface BrandConfigDetail {
  id: number;
  name: string;
  brandConfig: BrandConfig;
  categories: BrandCategory[];
  predictiveSuggestions: null|PredictiveSuggestionConfig[];
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  total: number;
  data: T;
}

export interface LogErrorEvent {
  event: string;
  message?: string;
  url?: string;
  code?: number;
  data?: any;
}

export interface BrandConfigResponse extends ApiResponse<BrandConfigDetail> { }

export interface SectionConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  sectionId: number;
  noneOfTheAbove: boolean;
  connectToAgent: boolean;
  loginRequired: boolean;
  showOpenRequest: boolean;
  showLiveChat: boolean;
  showVoiceChat: boolean;
  showResponse: boolean;
  responseMessage: string;
  transactionMessage: string;
  showEndMessage: boolean;
  endMessage: string;
  showToEndUser: boolean;
  new: boolean;
  createdBy: number;
  overrideParentConfig: boolean;
  skipAiChat?: boolean;
}

export interface NoneOfTheAboveConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  noneOfTheAbove: boolean;
  connectToAgent: boolean;
  loginRequired: boolean;
  showOpenRequest: boolean;
  showLiveChat: boolean;
  showVoiceChat: boolean;
  showResponse: boolean;
  responseMessage: string;
  transactionMessage: string;
  showEndMessage: boolean;
  endMessage: string;
  showToEndUser: boolean;
  new: boolean;
  createdBy: number;
  overrideParentConfig: boolean;
}

export interface Article {
  id: number;
  title: string;
  showToEndUser: boolean;
  checks: string|null;
  action: PleasedArticleAction|null;
  actionMessage: string|null;
  skipAiChat?: boolean;
}

export interface SearchArticle {
  id: number;
  brandId: number;
  sectionId: number;
  categoryId: number;
  categoryName: string;
  categoryNameTranslated: string;
  title: string;
  titleTranslated: string;
  position: number;
  isDraft: boolean;
  isPromoted: boolean;
  isActive: boolean;
  language: string;
  englishVersion: boolean;
  updatedAt: string;
}

export interface Section {
  id: number;
  title: string;
  showToEndUser: boolean;
  skipAiChat?: boolean;
}

export interface SectionDetail {
  id: number;
  title: string;
  sectionConfig: SectionConfig;
  noneOfTheAboveConfig: NoneOfTheAboveConfig & { sectionId: number | null };
  articles: Article[];
  sections: Section[];
}

export interface CategoryConfig {
  createdAt: string;
  updatedAt: string;
  updatedBy: number;
  id: number;
  categoryId: number;
  noneOfTheAbove: boolean;
  showResponse: boolean;
  responseMessage: string;
  iconUri: string;
  showToEndUser: boolean;
  showEndMessage: boolean;
  endMessage: string;
  showLiveChat: boolean;
  showOpenRequest: boolean;
  showVoiceChat: boolean;
  transactionMessage: string;
  connectToAgent: boolean;
  loginRequired: boolean;
  enableAbTesting: boolean;
  abValue: string;
  skipAiChat?: boolean;
  new: boolean;
  createdBy: number;
}

export interface CategoryDetail {
  id: number;
  title: string;
  categoryConfig: CategoryConfig;
  noneOfTheAboveConfig: NoneOfTheAboveConfig & { categoryId: number | null };
  sections: Section[];
  articles: Article[];
}

export interface ArticleConfig {
  new?: boolean;
  createdBy?: number;
  createdAt?: Date;
  updatedAt?: any;
  updatedBy?: any;

  id: number | string;
  title: string;
  articleId?: number;

  responseMessage?: string;
  showDescriptionBox?: boolean | null;
  descriptionMessage?: string;

  action?: PleasedArticleAction|null;
  actionMessage: string|null;
  actionText?: string|null;
  checks?: string | null;
  checkActionId?: string|number;
  transactionType?: string | null;
  transactionTypes?: string[];
  hideContent?: boolean | null;

  triggerTicketCreation?: boolean;
  ticketCreationResponseMessage?: string;

  overrideParentConnectAgent?: boolean;
  connectToAgent?: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;

  showConnectAgentMessage?: boolean;
  connectAgentMessage?: string;

  enableAbTesting?: boolean;
  abValue?: string;

  enableMetadataRules?: boolean;
  metadataRules?: MetadataRule[];

  skipAiChat?: boolean;

  category?: string | null;
  section?: string | null;
}

export interface ArticleParentConfig {
  id: number;
  type: string;
  title: string;
  showToEndUser?: boolean;
  language: string;
  connectToAgent: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
  skipAiChat?: boolean;
}

export interface ArticleDetailCategory {
  brandId: number;
  description: string;
  descriptionTranslated: string;
  englishVersion: boolean;
  id: number;
  isActive: boolean;
  language: string;
  position: number;
  title: string;
  titleTranslated: string;
}

export interface ArticleDetail {
  id: number;
  title: string;
  enTitle: string;
  body: string;
  articleConfig: ArticleConfig;
  category: ArticleDetailCategory;
  section?: ArticleDetailSection;
}

export interface ArticleDetailSection {
  id: number;
  brandId: number;
  categoryId: number;
  parentSectionId?: number;
  position: number;
  title: string;
  description: string;
  titleTranslated?: any;
  descriptionTranslated?: any;
  sections?: any;
  articles?: any;
  isActive: boolean;
  language: string;
  englishVersion: boolean;
}

export enum ArticleRate {
  HELPFUL = 'HELPFUL',
  NOT_HELPFUL = 'NOT_HELPFUL'
}

export interface ArticleRating {
  rating: ArticleRate | null;
}

export interface TicketField {
  fieldId: number;
  fieldValue: string;
}

export interface TicketFieldDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  displayName: string;
  placeholder: string;
  fieldName: string;
  isSystemField: boolean;
  fieldType: string;
  fieldValueOptions?: any;
  isMandatory: boolean;
  isUserEditable: boolean;
  isActive: boolean;
  fieldValueOptionsList?: any[];
  new: boolean;
  createdBy: number;
}

export interface MessageAttachment {
  id: string;
  fileName: string;
  url: string;
  contentType: string;
}

export interface UserSelection {
  createdAt: string;
  message: string;
  attachment?: MessageAttachment;
  isFromAI?: boolean;
}

export interface TicketCreateRequest {
  subject: string;
  description: string;
  recipientAddress: string;
  categoryId?: number;
  ticketFields: TicketField[];
  userSelections?: UserSelection[];
  userLogs?: ReportedEvent[]
  tags?: string[];
  transactionId?: string;
  chatTriggerId?: number;
  chatbotSessionId?: string;
}

export interface ExtraField {
  id: number;
  ticketId: number;
  fieldId: number;
  displayName: string;
  fieldName: string;
  fieldValue: string;
  isMandatory: boolean;
  isActive: boolean;
}

export interface VoiceExtraField {
  extraFieldKey: string;
  extraFieldValue: string;
}

export interface Author {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  foreignId?: any;
  email: string;
  phone: string;
  name: string;
  imageUrl?: any;
  roleId: number;
  sourceId: number;
  isActive: boolean;
  extraFields: Pick<ExtraField, 'fieldName' | 'fieldValue'> & Partial<ExtraField>[];
  defaultName: string;
  new: boolean;
  createdBy: number;
}

export interface TicketAttachment {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  fileName: string;
  url: string;
  contentType: string;
  isActive: boolean;
  isInline: boolean;
  ticketCommentId: number;
  new: boolean;
  createdBy: number;
}

export interface Comment {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  body: string;
  isPublic: boolean;
  sourceId: number;
  authorId: number;
  ticketId: number;
  attachments?: TicketAttachment[];
  author: Author;
  sourceName?: any;
  new: boolean;
  createdBy: number;
  bodyHtml: string;
}

export interface TicketDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  subject: string;
  emailSubject: string;
  priority: string;
  status: string;
  recipientAddress: string;
  receivedAddress: string;
  requesterId: number;
  assigneeId?: any;
  groupId?: any;
  emailCCIds?: any;
  slaTimestamp?: any;
  sourceId: number;
  rating?: any;
  brandId: number;
  categoryId: number;
  isAgentViewed?: any;
  isCreated: boolean;
  extraFields: ExtraField[];
  comments: Comment[];
  tags?: any;
  new: boolean;
  createdBy: number;
  chatTriggerResponse?: string;
}

export interface QueuePosition {
  position: number;
}

export interface UserInformation {
  id: string | number;
  foreignId: string | number;
  name: string;
  phone: string;
  email: string;
  token: string;
  greyListed: boolean;

  createdAt?: Date;
  createdBy?: number;
  updatedAt?: Date;
  updatedBy?: number;
  emailDomain?: string;
  imageUrl?: string;
  roleId?: number;
  sourceId?: number;
  isActive?: boolean;
  extraFields?: any[];
  refreshToken?: string;
  defaultName?: string;
  new?: boolean;
  greyListedReason?: string;
  userTierLevel?: number;
  userLoyaltyTierBand?: string;
  initialInteractions?: number;
  loginRestrict?: boolean;
  loyaltyHistoryHighestTier?: number;
  pocketRestrict?: boolean;
  prioritized?: boolean;
  telegramUser?: string | null;
  twitterUser?: string | null;
  userBand?: string;
}

export interface AgentsOnline {
  onlineAgents: boolean;
}

export interface OnlineAgentsCheck {
  onlineChatAgentsCheck?: boolean;
  onlineCallAgentsCheck?: boolean;
}

export interface TicketListQueryParams {
  requesterId?: number | string,
  offset: number,
  limit: number,
  status?: 'ACTIVE' | 'RESOLVED';
  updatedAtGreaterThan?: string;
  order?: 'ASC' | 'DESC',
  sortBy?: 'STATUS' | 'PRIORITY' | 'CREATED_AT' | 'UPDATED_AT' | 'SUBJECT' | 'REQUESTER'
}

export interface ListTicketDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  subject: string;
  emailSubject: string;
  priority: string;
  status: string;
  recipientAddress?: any;
  receivedAddress?: any;
  requesterId: number;
  assigneeId: number;
  groupId?: any;
  emailCCIds?: any;
  slaTimestamp?: any;
  sourceId: number;
  rating?: any;
  brandId: number;
  categoryId?: any;
  isAgentViewed?: any;
  requesterName: string;
  requesterPhone: string;
  requesterEmail: string;
  agentName: string;
  agentEmail: string;
  groupName?: any;
  extraFields?: any;
  comments?: any;
  new: boolean;
  createdBy: number;
}

export type ChatRateMode = 'chat' | 'voice' | 'ticket' | 'AI';

export interface EntityReview {
  type: string;
  comment: string;
  rating: number;
}

export interface ChatOverlayTrigger {
  message: string;
  chatTriggerId: number;
  actionType: {
    id: number;
  }
}

export interface ArticleMetrics {
  articleId: string;
  duration: number;
  abValue: string;
  connectToAgent: boolean;

  country?: string;
  userId?: string;
  endChat?: boolean;
  restartChat?: boolean;
}

export interface MetadataRule {
  id: number;
  title: string;
  metadataAutomation: string;
  timeSinceDirection?: 'LESS_THAN' | 'GREATER_THAN';
  timeSinceTransaction?: number;
  userGreyListed?: boolean;
  paymentProviderIds?: number[] | null;
  sessionIdExists?: boolean;
  tradeNumberExists?: boolean;
  transactionNumberExists?: boolean;
  nameConfirmed?: boolean;
  messages?: string;
  tag?: string;
  displayOrder: number;
}

export interface MetadataLog {
  logTraceId: string | null;
  transactionId: string;
  metadataAutomationId?: number;
  helpful?: boolean;
  connectedAgent?: boolean;
  messageShown?: boolean;
}

export interface GetAiChatResponseParams {
  is_guest_user?: boolean;
  user_id: string | number;
  session_id: string;
  user_input: string | null;
  first_request?: boolean;
  transaction_enable?: boolean;
  bet_enable?: boolean;
  userLogs?: ReportedEvent[]
  metadata?: Record<string, any>;
}

export enum AiResponseAction {
  CONNECT_TO_AGENT = 'connect_to_agent',
  END_CHAT = 'end_chat',
  TICKET_END_CHAT = 'ai_create_ticket_end_chat',

  SHOW_DEPOSIT_SELECTOR = 'show_deposit_selector',
  SHOW_PENDING_DEPOSIT_SELECTOR = 'show_pending_deposit_selector',
  SHOW_FAILED_DEPOSIT_SELECTOR = 'show_failed_deposit_selector',
  SHOW_SUCCESSFUL_DEPOSIT_SELECTOR = 'show_successful_deposit_selector',

  SHOW_WITHDRAWAL_SELECTOR = 'show_withdrawal_selector',
  SHOW_PENDING_WITHDRAWAL_SELECTOR = 'show_pending_withdrawal_selector',
  SHOW_FAILED_WITHDRAWAL_SELECTOR = 'show_failed_withdrawal_selector',
  SHOW_SUCCESSFUL_WITHDRAWAL_SELECTOR = 'show_successful_withdrawal_selector',
  SHOW_BETS_SELECTOR = 'show_bets_selector',
  SHOW_BET_SELECTOR = 'show_bet_selector',

  COLLECT_FILE = 'collect_file',
}

export interface AiChatResponsePayload {
  response: string | string[];
  // Outdated, do not use. TODO: remove after some time
  reference_titles: string[];
  // Outdated, do not use. TODO: remove after some time
  reference_contents: string[];
  // New field instead of two above. TODO: remove this comment with fields above
  reference: { article_id: number, body: string, hierarchy_path: string, title: string }[];
  // Firebase token for access to AI related FB refs (observe/intervene, etc)
  firebaseToken: string;
  action: AiResponseAction;
  errorCode?: number;
}

export interface SessionLog {
  sessionId: string;
  brandCode?: string;
  platform?: string;
  language?: string;
  userAgent?: string;
  requestType?: string;
  requestId?: number;
  sessionStartTime?: string;
  sessionEndTime?: string;
}

export interface EventLog {
  sessionId: string;
  eventId: string;
  eventType: string;
  eventName: string;
  eventTime: string;
  eventSelection?: string | number;
  metadata?: string;
}

export interface TransactionMetadata {
  sessionId?: string | null;
  isNameConfirmed?: boolean | null;
  transactionNumber?: string | null;
  tradeNumber?: string | null;
}

export type ExtendedSelectedTransStatement = TransStatement & TransactionMetadata & { transactionNotSelected: false, __USER_EVENTS?: any };
export type ExtendedTransStatement = ExtendedSelectedTransStatement | { transactionNotSelected: true };
export type UserMetadataForAi = Omit<PleasedUserInfo, 'token' | 'refreshToken'>;

export interface GuestUserInfo {
  email?: string;
  phone?: string;
  name?: string;
}

export interface GuestTokenInfo {
  sessionId: string;
  userToken: string;
  guestToken: string;
}

export enum ThumbRate {
  UP = 1,
  DOWN = 0
}

interface ThumbUpAiResponseParams {
  sessionId: string;
  messageId: string | number;
  isThumbUp: ThumbRate.UP;
}

interface ThumbDownAiResponseParams {
  sessionId: string;
  messageId: string | number;
  thumbDownReasonComment: string;
  thumbDownReason: string;
  thumbDownSubReasons?: string[];
  isThumbUp: ThumbRate.DOWN;
}

export interface Chat {
  chatId: number;
  customerId: number;
  firebaseToken: string;
  isAutoReplyEnabled: boolean;
  autoReplyMessage: string;
}

export type RateAiResponseParams = ThumbUpAiResponseParams | ThumbDownAiResponseParams;

export interface CategoryDetailResponse extends ApiResponse<CategoryDetail> { }

export interface SectionDetailResponse extends ApiResponse<SectionDetail> { }

export interface ArticleDetailResponse extends ApiResponse<ArticleDetail> { }

export interface RelatedArticlesResponse extends ApiResponse<SearchArticle[]> { }

export interface ArticleParentConfigResponse extends ApiResponse<ArticleParentConfig> { }

export interface ArticleSearchResponse extends ApiResponse<SearchArticle[]> { }

export interface ArticleRatingResponse extends ApiResponse<ArticleRating> { }

export interface TicketCreateResponse extends ApiResponse<TicketDetail> { }

export interface TicketFieldsResponse extends ApiResponse<TicketFieldDetail[]> { }

export interface QueuePositionResponse extends ApiResponse<QueuePosition> { }

export interface UserInformationResponse extends ApiResponse<UserInformation> { }

export interface AgentsOnlineResponse extends ApiResponse<AgentsOnline> { }

export interface OnlineAgentsCheckResponse extends ApiResponse<OnlineAgentsCheck> { }

export interface TicketListResponse extends ApiResponse<ListTicketDetail[]> { }

export interface EntityReviewResponse extends ApiResponse<{ reviews: EntityReview[] }> { }

export interface AddAttachmentResponse extends ApiResponse<TicketAttachment> { }

export interface ChatOverlayTriggerResponse extends ApiResponse<ChatOverlayTrigger> { }

export interface TransactionMetadataResponse extends ApiResponse<TransactionMetadata> { }

export interface AiChatResponse extends ApiResponse<AiChatResponsePayload> { }

export interface GetChatByIdResponse extends ApiResponse<Chat> { }

export interface ActiveTicketVoice {
  createAt: string | Date | number;
  duration: number;
  voiceChatRecordingUrl: string;
}
export interface ActiveTicketComment {
  commentId: number;
  attachments?: MessageAttachment[];
  authorName: string;
  authorType: 'AGENT' | 'CUSTOMER';
  body: string;
  bodyHtml: string;
  source: 'LIVE_CHAT' | 'VOICE_CHAT'
  createAt: string | Date | number;
  voice?: ActiveTicketVoice;
}
export interface ActiveTicket {
  id: number;
  subject: string;
  emailSubject: string;
  status: string;
  updatedAt: string | Date | number;
  commentCount: number;
  latestComment?: ActiveTicketComment;
}

export interface ActiveComments {
  comments: ActiveTicketComment[];
  ticket: ActiveTicket;
}

export interface ActiveTicketResponse extends ApiResponse<ActiveTicket[]> { };
export interface ActiveTicketCommentsResponse extends ApiResponse<ActiveComments> { };

type WaitTimes = '<1' | '<3' | '<5' | '<10'
export interface WaitingTime {
  averageChatWaitingTime: WaitTimes|null,
  averageCallWaitingTime: WaitTimes|null
}

export interface WaitTimeResponse extends ApiResponse<WaitingTime> {}

export interface EntryLogRequestBody {
  entryType: ApiOpenHelpEntry;
  url: string;
  device: string;
}

export interface ActiveOpenChat {
  id: number;
  chatStatus?: any;
  firebaseToken: string;
  customerId: number;
  customerName?: string;
  subject: string;
  assigneeId: number;
  assigneeName?: null|string;
  groupId?: null|number;
  brandId: number;
  brandName?: null|string;
  duration?: null|number;
  ongoingDuration?: null|number;
  ticketId?: null|number;
}

export interface ChatWarningField {
  id: number;
  configKey: string;
  configValue: string;
  brand: string;
  language: string;
}

export interface VerifyChatOpenDto {
  canResume: boolean;
  ticketId?: null|number;
  inActiveConversation: boolean;
  timeSinceTriggers?: SystemMessageConfig[];
}
export interface ActiveOpenChatResponse extends ApiResponse<ActiveOpenChat[]> {}

export interface CheckChatOpenResponse extends ApiResponse<VerifyChatOpenDto> {}
export interface FeedbackCategory {
  feedbackSelectionId: number;
  name: string;
}

export interface FeedbackCategoryResponse extends ApiResponse<FeedbackCategory[]> {}

export interface VoiceChatCsatResponse extends ApiResponse<boolean> {}

export interface ChatWarningsResponse extends ApiResponse<ChatWarningField[]> {}

export interface MetadataAutomationsResponse extends ApiResponse<MetadataRule[]> {}

export interface SystemMessageConfig {
  id: number;
  authorType: 'USER' | 'AGENT';
  message?: string;
  messages?: string[];
  value: number;
}

// OTP Verification Interfaces
export interface GuestOtpRequest {
  name: string;
}

export interface GuestTicketCreateRequest {
  email: string;
  code: string;
  name: string;
  subject: string;
  description: string;
  userLogs: any[];
  chatbotSessionId?: string;
}
